<template>
  <section>
    <nav class="columns">
      <div class="column">
        <b-field grouped group-multiline>
          <b-field>
            <p class="control">
              <span class="button is-static">
                {{ $t('attributes.activation_status') }}
              </span>
            </p>
            <b-select v-model="filterStatus" @input="fetchData">
              <option value="">{{ $t('attributes.all') }}</option>
              <option value="pending">
                {{ $t('attributes.firm_order_pending') }}
              </option>
              <option value="processing">
                {{ $t('attributes.firm_order_processing') }}
              </option>
              <option value="complete">
                {{ $t('attributes.firm_order_complete') }}
              </option>
            </b-select>
          </b-field>

          <b-field>
            <b-autocomplete
              v-model="collaborate_name"
              name="collaborate"
              field="name"
              :data="filteredCollaborates"
              :loading="isLoadingCollaborates"
              :placeholder="
                $t('attributes.collaborate') + $t('attributes.name')
              "
              :open-on-focus="true"
              @typing="filteringCollaborates"
              @select="onSelectCollaborate"
            >
              <template #empty>
                No results found
              </template>
            </b-autocomplete>
          </b-field>

          <b-field>
            <b-input
              v-model="search"
              placeholder="Search..."
              type="search"
              icon="magnify"
              @input="onFilter"
            ></b-input>
            <p class="control">
              <button class="button is-primary" @click="fetchData">
                {{ $t('attributes.search') }}
              </button>
            </p>
          </b-field>
        </b-field>
      </div>
    </nav>

    <b-table
      ref="table"
      :data="items"
      :loading="isLoading"
      :mobile-cards="true"
      hoverable
      narrowed
      scrollable
      detailed
      detail-key="id"
      :show-detail-icon="false"
      paginated
      pagination-position="both"
      backend-pagination
      backend-sorting
      :total="total"
      :per-page="perPage"
      :default-sort-direction="defaultSortOrder"
      :default-sort="[sortField, sortOrder]"
      aria-next-label="Next page"
      aria-previous-label="Previous page"
      aria-page-label="Page"
      aria-current-label="Current page"
      @page-change="onPageChange"
      @sort="onSort"
    >
      <b-table-column
        field="id"
        :label="$t('attributes.id')"
        width="95"
        numeric
        sortable
        v-slot="props"
      >
        <a @click="toggleDetail(props.row)">
          {{ props.row.id }}
        </a>
      </b-table-column>

      <b-table-column
        field="status"
        label="處理狀態"
        width="120"
        sortable
        centered
        v-slot="props"
      >
        {{ $t(`attributes.firm_order_${props.row.status}`) }}
      </b-table-column>

      <b-table-column
        field="collaborate_name"
        :label="$t('attributes.collaborate')"
        sortable
        v-slot="props"
      >
        <p style="white-space: nowrap;">
          {{ `[${props.row.collaborate_id}] ${props.row.collaborate_name}` }}
        </p>
      </b-table-column>

      <b-table-column field="account" label="登入帳號" sortable v-slot="props">
        <p style="white-space: nowrap;">
          {{ props.row.account }}
        </p>
      </b-table-column>

      <b-table-column
        field="demand"
        label="需求"
        width="120"
        centered
        v-slot="props"
      >
        {{ demandList(props.row) }}
      </b-table-column>

      <b-table-column field="username" label="聯絡人" sortable v-slot="props">
        {{ props.row.username }}
      </b-table-column>

      <b-table-column field="number" label="員工編號" sortable v-slot="props">
        {{ props.row.number }}
      </b-table-column>

      <b-table-column
        field="department"
        label="服務單位"
        sortable
        v-slot="props"
      >
        {{ props.row.department }}
      </b-table-column>

      <b-table-column field="tel" label="聯絡電話" sortable v-slot="props">
        {{ props.row.tel }}
      </b-table-column>

      <b-table-column field="mobile" label="手機號碼" sortable v-slot="props">
        {{ props.row.mobile }}
      </b-table-column>

      <b-table-column field="email" label="聯絡信箱" sortable v-slot="props">
        {{ props.row.email }}
      </b-table-column>

      <b-table-column field="lineId" label="Line ID" sortable v-slot="props">
        {{ props.row.lineId }}
      </b-table-column>

      <b-table-column
        field="created_at"
        :label="$t('attributes.create_date')"
        v-slot="props"
      >
        {{ formatDateTime(props.row.created_at) }}
      </b-table-column>

      <b-table-column
        :label="$t('attributes.function')"
        width="200"
        centered
        v-slot="props"
      >
        <b-button
          type="is-warning"
          @click.stop.prevent="toggleDetail(props.row)"
        >
          {{ $t('attributes.process') }}
        </b-button>
      </b-table-column>

      <template #detail="props">
        <firm-order-detail :order="props.row" @updated="updatedOrder" />
      </template>

      <template #empty>
        <section class="section">
          <div class="content has-text-grey has-text-centered">
            <p>
              <b-icon icon="emoticon-sad" size="is-large" />
            </p>
            <p>{{ $t('attributes.empty') }}</p>
          </div>
        </section>
      </template>
    </b-table>
  </section>
</template>

<script>
import debounce from 'lodash/debounce'
import notifyService from '@/services/notifyService'
import {
  FIRM_COLLABORATE_ORDERS_API,
  FIRM_COLLABORATES_API
} from '@/utils/apis'
import FirmOrderDetail from '@/components/firm/FirmOrderDetail.vue'

export default {
  name: 'FirmOrderList',
  components: { FirmOrderDetail },

  data() {
    return {
      statusOptions: ['pending', 'processing', 'complete'],
      featureName: {
        tickets: '代訂機栗',
        internal: '國內旅遊',
        free: '機＋酒自由行',
        group: '團體旅遊',
        hotel: '代訂飯店',
        other: '其他'
      },

      isLoadingOrders: false,
      items: [],
      total: 0,
      filterStatus: '',
      search: '',
      sortField: 'id',
      sortOrder: 'desc',
      defaultSortOrder: 'asc',
      page: 1,
      perPage: 10,

      isLoadingCollaborates: false,
      collaborate_name: '',
      collaborates: [],
      filteredCollaborates: [],
      selectedCollaborate: null,

      selected: null,
      sending: false
    }
  },
  computed: {
    isLoading() {
      return this.isLoadingCollaborates || this.isLoadingOrders
    }
  },
  mounted() {
    this.loadCollaborates()
  },
  methods: {
    async fetchData() {
      const api = `${FIRM_COLLABORATE_ORDERS_API}`

      const params = {
        collaborate_id: this.selectedCollaborate && this.selectedCollaborate.id,
        status: this.filterStatus,
        search: this.search,
        sort_by: this.sortField,
        by_descending: this.sortOrder !== 'asc',
        page_size: this.perPage,
        page_number: this.page
      }

      this.isLoadingOrders = true
      try {
        const res = await this.axios.get(api, { params })
        const data = res.data.data

        this.items = data.items
        this.total = data.item_count
      } catch (e) {
        this.items = []
        this.total = 0

        notifyService.error('抓取訂單列表失敗', e)
      } finally {
        this.isLoadingOrders = false
      }
    },
    toggleDetail(row) {
      this.$refs.table.toggleDetails(row)
    },
    updatedOrder(order) {
      this.items = this.items.map(item => (item.id === order.id ? order : item))
    },
    async loadCollaborates() {
      const api = `${FIRM_COLLABORATES_API}/list`

      this.isLoadingCollaborates = true
      try {
        const res = await this.axios.get(api)
        this.collaborates = res.data.data
        this.filteringCollaborates()

        await this.fetchData()
      } catch (e) {
        this.collaborates = []
        this.filteredCollaborates = []
        this.selectedCollaborate = null

        notifyService.fetch_error('collaborate', e)
      } finally {
        this.isLoadingCollaborates = false
      }
    },
    filteringCollaborates: debounce(function() {
      this.filteredCollaborates = this.collaborates.filter(option => {
        return (
          option.name
            .toString()
            .toLowerCase()
            .indexOf(this.collaborate_name.toLowerCase()) >= 0
        )
      })
    }, 500),
    onSelectCollaborate(option) {
      this.selectedCollaborate = option
      this.fetchData()
    },
    onFilter: debounce(function() {
      this.fetchData()
    }, 500),
    onPageChange(page) {
      this.page = page
      this.fetchData()
    },
    onSort(field, order) {
      this.sortField = field
      this.sortOrder = order
      this.fetchData()
    },
    demandList(order) {
      const demand = JSON.parse(order.demand)
      const demands = demand.map(d => this.featureName[d]).join(',')
      return demands || '無'
    }
  }
}
</script>
